import { captureException } from "@sentry/react";
import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  CommunityFeedItemV2DtoPaginationResultDto,
  PollCreateRequest,
  PollDto,
  PollUpdateRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

import { usePollQueries } from "./queryOptions";

const useDeletePoll = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (pollId: string) => api.deletePollsByIdV1(pollId),
    onSuccess: async () => {
      showFlashToast({
        variant: "success",
        message: t("component.community-poll.delete.success"),
      });
      await queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.MESSAGES(projectId), "FEED"],
      });
    },
    onError: (error) => {
      captureException(error);
      showFlashToast({
        variant: "alert",
        message: t("component.community-poll.delete.error"),
      });
    },
  });
};

const useAddPollVotes = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const pollQueries = usePollQueries();

  return useMutation({
    mutationFn: ({ pollId, data }: { pollId: string; data: { votes: string[] } }) =>
      api.postPollsVoteV1(pollId, data.votes),
    onSuccess: async (_, payload) => {
      // Fetch latest poll answers upon request success
      const newAnswers = await queryClient.fetchQuery(pollQueries.answers(payload.pollId));

      // Optimistically update the poll results in the feed
      queryClient.setQueriesData<InfiniteData<CommunityFeedItemV2DtoPaginationResultDto>>(
        {
          queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
        },
        (currData) => {
          if (!currData) return currData;

          const updatedPages = currData.pages.map((page) => {
            const updatedItems = page.items.map((item) => {
              if (item.type !== "poll" || item.poll?.id !== payload.pollId) return item;

              return {
                ...item,
                poll: {
                  ...item.poll,
                  results: newAnswers.data.pollResults,
                  canRespond: "alreadyAnswered",
                } satisfies PollDto,
              };
            });

            return {
              ...page,
              items: updatedItems,
            };
          });

          return {
            ...currData,
            pages: updatedPages,
          };
        },
      );

      showFlashToast({
        variant: "success",
        message: t("component.community-poll.vote.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.community-poll.vote.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
      });
    },
  });
};

const useUpdatePollReadStatus = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (pollId: string) => api.postPollsReadV1(pollId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.MESSAGES(projectId), "FEED"],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MESSAGES_FEED_STATS(projectId),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.community-poll.mark-as-read.error"),
      });
    },
  });
};

const useDeletePollVotes = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessionUser = useSessionUser();
  const projectId = useProjectId();
  const pollQueries = usePollQueries();

  return useMutation({
    mutationFn: (pollId: string) => api.deletePollsVoteV1(pollId),
    onSuccess: async (_, pollId) => {
      // Fetch latest poll answers upon request success
      const newAnswers = await queryClient.fetchQuery(pollQueries.answers(pollId));

      // Optimistically update the poll results in the feed
      queryClient.setQueriesData<InfiniteData<CommunityFeedItemV2DtoPaginationResultDto>>(
        {
          queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
        },
        (currData) => {
          if (!currData) return currData;

          const updatedPages = currData.pages.map((page) => {
            const updatedItems = page.items.map((item) => {
              if (item.type !== "poll" || item.poll?.id !== pollId) return item;

              const canSeeResults =
                sessionUser.isSuperAdmin ||
                item.poll.createdBy.id === sessionUser.id ||
                Boolean(item.poll.endsAt && new Date() > new Date(item.poll.endsAt));

              return {
                ...item,
                poll: {
                  ...item.poll,
                  results: newAnswers.data.pollResults,
                  canRespond: "yes",
                  canSeeResults,
                } satisfies PollDto,
              };
            });

            return {
              ...page,
              items: updatedItems,
            };
          });

          return {
            ...currData,
            pages: updatedPages,
          };
        },
      );

      showFlashToast({
        variant: "success",
        message: t("component.community-poll.clear-vote.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.community-poll.clear-vote.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
      });
    },
  });
};

const useAddPoll = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { data: PollCreateRequest }) => api.postPollsV1(payload.data).then((x) => x.data),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, res.id) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.message-feed.create.notifications.error") });
    },
  });
};

const useUpdatePoll = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { pollId: string; data: PollUpdateRequest }) => api.putPollsV1(payload.pollId, payload.data),
    onSuccess: async (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.message-feed.edit.poll.notifications.success") });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, payload.pollId) });
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.MESSAGES(projectId), "FEED"] });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.message-feed.edit.poll.notifications.error") });
    },
  });
};

export const pollMutations = {
  useAddPoll,
  useDeletePoll,
  useUpdatePoll,
  useUpdatePollReadStatus,
  useAddPollVotes,
  useDeletePollVotes,
};
